import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { ApiUser } from "src/api/api_user";
import { Strong, StrongText, Text } from "src/components/base/text";
import { Button } from "src/components/base/button";
import { EllipsisVerticalIcon, EnvelopeIcon, FingerPrintIcon, KeyIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Divider } from "src/components/base/divider";
import { Badge } from "src/components/base/badge";
import ChangePasswordDialog from "src/components/dialogs/change-password";
import { Subheading } from "src/components/base/heading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import AddWebauthKeyDialog from "src/components/dialogs/add-webauth-key";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { UUID } from "src/api/api_public";
import CreatePasswordDialog from "src/components/dialogs/create-password";
import AddNewTotpKeyDialog from "src/components/dialogs/add-new-totp";
import { toast } from "react-toastify/unstyled";
import CreatePasswordWithTotpDialog from "src/components/dialogs/create-password-with-totp";

/**
 * The properties for {@link PasswordAndAuthenticationView}
 */
export type PasswordAndAuthenticationViewProps = {};

/**
 * View for editing the password and authentication
 */
export default function PasswordAndAuthenticationView(props: PasswordAndAuthenticationViewProps) {
    const [t] = useTranslation("user-authentication");
    const [tg] = useTranslation();

    const [createPassword, setCreatePassword] = React.useState(false);
    const [createPasswordWithTotp, setCreatePasswordWithTotp] = React.useState(false);
    const [changePassword, setChangePassword] = React.useState(false);
    const [deletePassword, setDeletePassword] = React.useState(false);

    const [createWebauthKey, setCreateWebauthKey] = React.useState(false);
    const [deleteWebauthKey, setDeleteWebauthKey] = React.useState<UUID | undefined>(undefined);

    const [createTOTPKey, setCreateTOTPKey] = React.useState(false);
    const [deleteTOTPKey, setDeleteTOTPKey] = React.useState<UUID | undefined>(undefined);

    const router = useRouter();
    const data = Route.useLoaderData();

    /**
     * Api call to delete the password
     */
    const deletePasswordCall = async () => {
        await ApiUser.credentials.deletePassword();
        await router.invalidate();
        setDeletePassword(false);
        toast.success(t("toast.success-delete-password"));
    };

    const deleteWebauthkeyCall = async (key: UUID) => {
        await ApiUser.credentials.deleteWebauthnKey(key);
        await router.invalidate();
        setDeleteWebauthKey(undefined);
        toast.success(t("toast.success-delete-webauth"));
    };

    const deleteTOTPKeyCall = async (key: UUID) => {
        await ApiUser.credentials.deleteTotp(key);
        await router.invalidate();
        setDeleteTOTPKey(undefined);
        toast.success(t("toast.success-delete-totp"));
    };

    return (
        <HeadingLayout heading={t("heading.password-authentication")}>
            <Subheading>{t("heading.sign-in-methods")}</Subheading>
            <div className={"flex max-w-2xl flex-col rounded-lg border border-zinc-950/10 dark:border-white/10"}>
                <div className={"flex items-center justify-between p-4"}>
                    <div className={"flex items-center gap-4"}>
                        <Text>
                            <EnvelopeIcon className={"size-6"} />
                        </Text>
                        <Strong>{t("label.email")}</Strong>
                    </div>
                    <Strong>{data.mail}</Strong>
                </div>
                <Divider />
                {data.requirements !== "Passwordless" && (
                    <>
                        <div className={"flex items-center justify-between gap-4 p-4"}>
                            <div className={"flex items-center gap-4"}>
                                <Text>
                                    <KeyIcon className={"size-6"} />
                                </Text>
                                <Strong className={"flex flex-col gap-1"}>
                                    {t("label.password")}
                                    {data.password ? (
                                        <Badge color={"blue"} className={"w-fit"}>
                                            {t("label.configured")}
                                        </Badge>
                                    ) : (
                                        <Badge className={"w-fit"}>{t("label.not-configured")}</Badge>
                                    )}
                                </Strong>
                            </div>
                            <div className={"flex items-center gap-2"}>
                                {data.password ? (
                                    <>
                                        <Button outline={true} onClick={() => setChangePassword(true)}>
                                            {t("button.change-password")}
                                        </Button>
                                        <Button
                                            onClick={() => setDeletePassword(true)}
                                            disabled={!data.can_delete_sign_in_method}
                                            outline={true}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        outline={true}
                                        onClick={() => {
                                            if (data.requirements === "None") {
                                                setCreatePassword(true);
                                            } else if (data.requirements === "MultiFactor") {
                                                setCreatePasswordWithTotp(true);
                                            }
                                        }}
                                    >
                                        {t("button.create-password")}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <Divider />
                    </>
                )}
                <div className={"grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 p-4"}>
                    <Text className={"flex items-center justify-center"}>
                        <FingerPrintIcon className={"size-6"} />
                    </Text>
                    <Strong className={"flex flex-col gap-1"}>
                        {t("label.webauth-key")}
                        {data.webauth_keys.length > 0 ? (
                            <Badge color={"blue"} className={"w-fit"}>
                                {t("label.configured")}
                            </Badge>
                        ) : (
                            <Badge className={"w-fit"}>{t("label.not-configured")}</Badge>
                        )}
                    </Strong>
                    <Button
                        onClick={() => setCreateWebauthKey(true)}
                        outline={true}
                        className={"h-fit place-self-center"}
                    >
                        {t("button.add-webauth-key")}
                    </Button>
                    {data.webauth_keys.length > 0 &&
                        data.webauth_keys.map((key) => (
                            <div key={key.uuid} className={"col-span-2 col-start-2 flex flex-col"}>
                                <Divider />
                                <div className={"flex justify-between px-4 pt-1"}>
                                    <div className={"flex flex-col gap-1"}>
                                        <StrongText>{key.label}</StrongText>
                                        <Text>
                                            {t("label.added-to", {
                                                object: new Date(key.created_at).toLocaleDateString("de-DE", {
                                                    month: "long",
                                                    day: "numeric",
                                                    year: "numeric",
                                                }),
                                            })}
                                        </Text>
                                    </div>
                                    <div className={"flex items-center justify-end"}>
                                        <Button
                                            disabled={!data.can_delete_sign_in_method}
                                            outline={true}
                                            onClick={() => setDeleteWebauthKey(key.uuid)}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>
            </div>
            {data.requirements !== "Passwordless" && (
                <>
                    <Divider />

                    <div className={"flex justify-between"}>
                        <Subheading>{t("heading.two-factor")}</Subheading>
                        <Button disabled={!data.password} outline={true} onClick={() => setCreateTOTPKey(true)}>
                            {t("button.create-totp")}
                        </Button>
                    </div>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{t("label.label")}</TableHeader>
                                <TableHeader>{t("label.created-at")}</TableHeader>
                                <TableHeader className={"w-0"}>
                                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                                </TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.totp_keys.map((key) => (
                                <TableRow key={key.uuid}>
                                    <TableCell>{key.label}</TableCell>
                                    <TableCell>{new Date(key.created_at).toLocaleString("de-DE")}</TableCell>
                                    <TableCell>
                                        <Dropdown>
                                            <DropdownButton plain={true}>
                                                <EllipsisVerticalIcon />
                                            </DropdownButton>
                                            <DropdownMenu>
                                                <DropdownSection>
                                                    <DropdownHeading>{tg("heading.danger-zone")}</DropdownHeading>
                                                    <DropdownItem
                                                        disabled={
                                                            data.requirements === "MultiFactor" &&
                                                            data.totp_keys.length === 1
                                                        }
                                                        onClick={() => setDeleteTOTPKey(key.uuid)}
                                                    >
                                                        <TrashIcon />
                                                        {t("button.delete-totp")}
                                                    </DropdownItem>
                                                </DropdownSection>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}

            <Suspense>
                <CreatePasswordDialog
                    isOpen={createPassword}
                    endpoint={"user"}
                    onClose={async () => setCreatePassword(false)}
                    onSubmit={async () => {
                        await router.invalidate();
                        setCreatePassword(false);
                        setCreateTOTPKey(true);
                    }}
                />

                <CreatePasswordWithTotpDialog
                    isOpen={createPasswordWithTotp}
                    mail={data.mail}
                    onClose={async () => setCreatePasswordWithTotp(false)}
                    onSubmit={async () => {
                        toast.success(t("toast.success-create-password"));
                        await router.invalidate();
                        setCreatePasswordWithTotp(false);
                    }}
                />

                <ChangePasswordDialog isOpen={changePassword} onClose={() => setChangePassword(false)} />

                <AddWebauthKeyDialog
                    type={"user"}
                    onClose={async () => {
                        await router.invalidate();
                        setCreateWebauthKey(false);
                    }}
                    isOpen={createWebauthKey}
                />

                <AddNewTotpKeyDialog
                    isOpen={createTOTPKey}
                    endpoint={"user"}
                    onSubmit={() => {
                        router.invalidate();
                        setCreateTOTPKey(false);
                    }}
                    onClose={() => setCreateTOTPKey(false)}
                    mail={data.mail}
                />
            </Suspense>
            {deleteWebauthKey && (
                <Dialog open={true} onClose={() => setDeleteWebauthKey(undefined)}>
                    <DialogTitle>{t("heading.delete-webauth-key")}</DialogTitle>
                    <DialogActions>
                        <Button autoFocus={true} onClick={() => setDeleteWebauthKey(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button color={"red"} onClick={async () => await deleteWebauthkeyCall(deleteWebauthKey)}>
                            {tg("button.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {deletePassword && (
                <Dialog open={true} onClose={() => setDeletePassword(false)}>
                    <DialogTitle>{t("heading.delete-password")}</DialogTitle>
                    <DialogDescription>{t("description.delete-password")}</DialogDescription>
                    <DialogActions>
                        <Button autoFocus={true} onClick={() => setDeletePassword(false)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button color={"red"} onClick={async () => await deletePasswordCall()}>
                            {tg("button.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {deleteTOTPKey && (
                <Dialog open={true} onClose={() => setDeleteTOTPKey(undefined)}>
                    <DialogTitle>{t("heading.delete-totp-key")}</DialogTitle>
                    <DialogActions>
                        <Button autoFocus={true} onClick={() => setDeleteTOTPKey(undefined)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button color={"red"} onClick={async () => deleteTOTPKeyCall(deleteTOTPKey)}>
                            {tg("button.delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </HeadingLayout>
    );
}

export const Route = createFileRoute("/_user/u/authentication/")({
    component: PasswordAndAuthenticationView,
    loader: async () => ApiUser.credentials.get(),
});
